import { useCallback, useReducer, useRef } from "react";
import { StartGuideStep } from "@/features/pathpick/features/start-guide/types";
import { DEMO_PRODUCT_IDS } from "@/features/pathpick/features/start-guide/utils/demoProducts";

// 등록(연동/파일 생성) 목 처리 상태.
export type DemoRegistrationStatus = "idle" | "processing" | "done";

// 등록 방식 — KREAM 전용("none")은 등록 자체가 없다.
export type DemoRegistrationMethod = "api" | "file" | "none";

// 상품별 상태 — 등록 방식과 이후 관리 동작(연동 해제/삭제)을 함께 담는다.
export type DemoProductState =
  | "saved"
  | "linked"
  | "file-ready"
  | "unlinked"
  | "deleted";

export interface StartGuideDemoState {
  // 담기를 누른 상품 id
  pickedIds: number[];
  // 담은 상품 화면에서 체크된 상품 id
  selectedIds: number[];
  // 주문 체험을 마친 상품 id — 카드의 `주문 완료` 표시와 완료 판정에 쓴다.
  orderedIds: number[];
  // KREAM 주문 상세 안내에서 파일 생성을 신청했는지 — 완료 패널과 다음 단계 판정에 쓴다.
  hasRequestedKreamFile: boolean;
  // 스마트스토어 등록 예시를 열어 봤는지 — 담은 상품 화면의 다음 단계 판정에 쓴다.
  hasViewedRegistrationExample: boolean;
  registrationStatus: DemoRegistrationStatus;
  // 사용자가 등록 CTA를 눌러 실제로 목 처리를 마쳤는지.
  // prepareForStep 이 뒤 단계를 위해 채워 두는 "완료 가정"과 구분하려고 따로 둔다.
  hasRunRegistration: boolean;
  productStateById: Record<number, DemoProductState>;
}

const INITIAL_STATE: StartGuideDemoState = {
  pickedIds: [],
  selectedIds: [],
  orderedIds: [],
  hasRequestedKreamFile: false,
  hasViewedRegistrationExample: false,
  registrationStatus: "idle",
  hasRunRegistration: false,
  productStateById: {},
};

type Action =
  | { type: "PICK"; id: number }
  | { type: "TOGGLE_SELECT"; id: number }
  | { type: "START_REGISTRATION" }
  | {
      type: "FINISH_REGISTRATION";
      method: Exclude<DemoRegistrationMethod, "none">;
    }
  | { type: "MANAGE_PRODUCT"; id: number; operation: "unlink" | "delete" }
  | { type: "COMPLETE_ORDER"; ids: number[] }
  | { type: "REQUEST_KREAM_FILE" }
  | { type: "VIEW_REGISTRATION_EXAMPLE" }
  | { type: "RESET" }
  | {
      type: "PREPARE_FOR_STEP";
      step: StartGuideStep;
      method: DemoRegistrationMethod;
    };

const appendUnique = (current: number[], additions: number[]) => [
  ...current,
  ...additions.filter((id) => !current.includes(id)),
];

const withProductState = (
  current: Record<number, DemoProductState>,
  ids: number[],
  state: DemoProductState,
  // 이미 값이 있으면 덮지 않는 모드 — 뒤 단계를 열 때 기존 진행을 지우지 않기 위해서다.
  overwrite = true
) =>
  ids.reduce<Record<number, DemoProductState>>(
    (next, id) => {
      if (overwrite || next[id] === undefined) {
        next[id] = state;
      }
      return next;
    },
    { ...current }
  );

/**
 * 뒤 단계를 탭으로 바로 열었을 때 그 화면이 성립할 최소 상태만 채운다(전달서 §4).
 *
 * KREAM 전용(`none`)은 등록 단계가 없으므로 어떤 경우에도 등록 완료로 올리지 않는다 —
 * 등록한 적 없는 사용자에게 "연동 완료"가 보이면 안 된다.
 *
 * 채워 넣은 완료는 "가정"이지 사용자의 진행이 아니다. 그래서 담은 상품 화면으로
 * 돌아오면 실제로 실행한 적(`hasRunRegistration`)이 없는 한 그 가정을 되돌린다.
 */
function prepareForStep(
  state: StartGuideDemoState,
  step: StartGuideStep,
  method: DemoRegistrationMethod
): StartGuideDemoState {
  // 상품 찾기까지는 준비할 것이 없다 — 담기부터 직접 해 보는 화면이다.
  if (step === "setup" || step === "product_tour") {
    return state;
  }

  const pickedIds = appendUnique(state.pickedIds, DEMO_PRODUCT_IDS);
  const selectedIds = appendUnique(state.selectedIds, DEMO_PRODUCT_IDS);
  const withProducts = {
    ...state,
    pickedIds,
    selectedIds,
    productStateById: withProductState(
      state.productStateById,
      DEMO_PRODUCT_IDS,
      "saved",
      false
    ),
  };

  // 담은 상품 화면은 사용자가 실제로 실행한 진행만 보여준다 —
  // 뒤 단계를 열며 채워 둔 완료 가정은 여기로 돌아오면 되돌린다.
  // 실행한 적 없는 사용자에게 "파일 생성 완료"가 보이면 안 된다.
  if (step === "workbench") {
    return state.hasRunRegistration
      ? withProducts
      : {
          ...withProducts,
          registrationStatus: "idle",
          hasViewedRegistrationExample: false,
          productStateById: withProductState(
            withProducts.productStateById,
            DEMO_PRODUCT_IDS,
            "saved"
          ),
        };
  }

  if (method === "none") {
    return withProducts;
  }

  if (withProducts.registrationStatus === "done") {
    return withProducts;
  }

  return {
    ...withProducts,
    registrationStatus: "done",
    // 등록을 이미 지난 것으로 치는 화면이라 등록 예시 열람도 마친 것으로 둔다.
    // 담은 상품 화면으로 돌아가면 위에서 이 가정을 통째로 되돌린다.
    hasViewedRegistrationExample: true,
    productStateById: withProductState(
      withProducts.productStateById,
      DEMO_PRODUCT_IDS,
      method === "api" ? "linked" : "file-ready",
      false
    ),
  };
}

function reducer(
  state: StartGuideDemoState,
  action: Action
): StartGuideDemoState {
  switch (action.type) {
    case "PICK":
      // 담기는 저장만 한다 — 선택은 담은 상품 화면에서 사용자가 직접 한다(전달서 §10).
      return state.pickedIds.includes(action.id)
        ? state
        : {
            ...state,
            pickedIds: [...state.pickedIds, action.id],
            productStateById: withProductState(
              state.productStateById,
              [action.id],
              "saved",
              false
            ),
          };
    case "TOGGLE_SELECT":
      return {
        ...state,
        selectedIds: state.selectedIds.includes(action.id)
          ? state.selectedIds.filter((id) => id !== action.id)
          : [...state.selectedIds, action.id],
      };
    case "START_REGISTRATION":
      return { ...state, registrationStatus: "processing" };
    case "FINISH_REGISTRATION":
      return {
        ...state,
        registrationStatus: "done",
        hasRunRegistration: true,
        productStateById: withProductState(
          state.productStateById,
          state.selectedIds,
          action.method === "api" ? "linked" : "file-ready"
        ),
      };
    case "MANAGE_PRODUCT":
      // 연동된 상품에만 의미가 있다 — 이미 해제·삭제한 행은 다시 바뀌지 않는다.
      if (state.productStateById[action.id] !== "linked") {
        return state;
      }
      return {
        ...state,
        productStateById: {
          ...state.productStateById,
          [action.id]: action.operation === "unlink" ? "unlinked" : "deleted",
        },
      };
    case "COMPLETE_ORDER":
      return {
        ...state,
        orderedIds: appendUnique(state.orderedIds, action.ids),
      };
    case "REQUEST_KREAM_FILE":
      return { ...state, hasRequestedKreamFile: true };
    case "VIEW_REGISTRATION_EXAMPLE":
      return { ...state, hasViewedRegistrationExample: true };
    case "PREPARE_FOR_STEP":
      return prepareForStep(state, action.step, action.method);
    case "RESET":
      return INITIAL_STATE;
    default:
      return state;
  }
}

/**
 * 튜토리얼 안에서만 사는 목 상태.
 *
 * 브라우저 저장소에 남기지 않는다(전달서 §18 — 새로고침 후 체험 상태까지 복구할 필요 없음).
 * 무엇보다 실제 담은 상품·등록 신청·주문 내역과 완전히 분리돼 있어서,
 * 여기서 무엇을 하든 react-query 캐시나 서버 상태에는 닿지 않는다.
 */
export default function useStartGuideDemoState() {
  const [demo, dispatch] = useReducer(reducer, INITIAL_STATE);
  // 목 처리 타이머 — 처리 중에 초기화하면 완료 dispatch 가 뒤늦게 도착하면 안 된다.
  const registrationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const pick = useCallback((id: number) => dispatch({ type: "PICK", id }), []);
  const toggleSelect = useCallback(
    (id: number) => dispatch({ type: "TOGGLE_SELECT", id }),
    []
  );
  const manageProduct = useCallback(
    (id: number, operation: "unlink" | "delete") =>
      dispatch({ type: "MANAGE_PRODUCT", id, operation }),
    []
  );
  const completeOrder = useCallback(
    (ids: number[]) => dispatch({ type: "COMPLETE_ORDER", ids }),
    []
  );
  const requestKreamFile = useCallback(
    () => dispatch({ type: "REQUEST_KREAM_FILE" }),
    []
  );
  const viewRegistrationExample = useCallback(
    () => dispatch({ type: "VIEW_REGISTRATION_EXAMPLE" }),
    []
  );
  const prepareForStep = useCallback(
    (step: StartGuideStep, method: DemoRegistrationMethod) =>
      dispatch({ type: "PREPARE_FOR_STEP", step, method }),
    []
  );
  // 저장 상태를 초기화할 때 체험 진행도 같이 비운다 — 둘이 어긋나면 안 된다.
  const resetDemo = useCallback(() => {
    if (registrationTimerRef.current !== null) {
      clearTimeout(registrationTimerRef.current);
      registrationTimerRef.current = null;
    }
    dispatch({ type: "RESET" });
  }, []);

  // 등록 목 처리 — 전달서 §10 이 요구하는 300~600ms 로딩 뒤 성공만 구현한다.
  const runRegistration = useCallback(
    (method: Exclude<DemoRegistrationMethod, "none">) => {
      dispatch({ type: "START_REGISTRATION" });
      registrationTimerRef.current = setTimeout(() => {
        registrationTimerRef.current = null;
        dispatch({ type: "FINISH_REGISTRATION", method });
      }, 450);
    },
    []
  );

  return {
    demo,
    pick,
    toggleSelect,
    runRegistration,
    manageProduct,
    completeOrder,
    requestKreamFile,
    viewRegistrationExample,
    prepareForStep,
    resetDemo,
  };
}
